import React from 'react';
import { useI18n } from '../hooks/useI18n';
import { AlertTriangle, X } from 'lucide-react';

interface CashThresholdWarningModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    dailyTotal: number;
    monthlyTotal: number;
    dailyThreshold: number;
    monthlyThreshold: number;
    invoiceAmount: number;
    currency: string;
}

const CashThresholdWarningModal: React.FC<CashThresholdWarningModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    dailyTotal,
    monthlyTotal,
    dailyThreshold,
    monthlyThreshold,
    invoiceAmount,
    currency
}) => {
    const { t, isRTL } = useI18n();

    if (!isOpen) return null;

    const newDailyTotal = dailyTotal + invoiceAmount;
    const newMonthlyTotal = monthlyTotal + invoiceAmount;
    const dailyExceeded = newDailyTotal > dailyThreshold;
    const monthlyExceeded = newMonthlyTotal > monthlyThreshold;

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'MAD',
        }).format(amount);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
                className={`bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 ${isRTL ? 'rtl' : 'ltr'}`}
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-yellow-50">
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
                        <div className="p-2 bg-yellow-100 rounded-full">
                            <AlertTriangle className="w-6 h-6 text-yellow-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {t('invoices.cashThresholdWarning')}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-600 mb-6">
                        {t('invoices.cashThresholdExceeded')}
                    </p>

                    <div className="space-y-4">
                        {/* Invoice Amount */}
                        <div className="bg-blue-50 p-3 rounded-lg">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-blue-700">
                                    {t('invoices.invoiceAmount')}
                                </span>
                                <span className="text-lg font-bold text-blue-900">
                                    {formatCurrency(invoiceAmount)}
                                </span>
                            </div>
                        </div>

                        {/* Daily Threshold */}
                        <div className={`p-3 rounded-lg ${dailyExceeded ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">
                                    {t('invoices.dailyThreshold')}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                    {formatCurrency(dailyThreshold)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600">
                                    {t('invoices.currentDailyTotal')}
                                </span>
                                <span className="text-sm text-gray-900">
                                    {formatCurrency(dailyTotal)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">
                                    {t('invoices.newDailyTotal')}
                                </span>
                                <span className={`text-sm font-semibold ${dailyExceeded ? 'text-red-600' : 'text-green-600'}`}>
                                    {formatCurrency(newDailyTotal)}
                                </span>
                            </div>
                            {dailyExceeded && (
                                <p className="text-xs text-red-600 mt-2 font-medium">
                                    ⚠️ {t('invoices.dailyLimitExceeded')}
                                </p>
                            )}
                        </div>

                        {/* Monthly Threshold */}
                        <div className={`p-3 rounded-lg ${monthlyExceeded ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">
                                    {t('invoices.monthlyThreshold')}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                    {formatCurrency(monthlyThreshold)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600">
                                    {t('invoices.currentMonthlyTotal')}
                                </span>
                                <span className="text-sm text-gray-900">
                                    {formatCurrency(monthlyTotal)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">
                                    {t('invoices.newMonthlyTotal')}
                                </span>
                                <span className={`text-sm font-semibold ${monthlyExceeded ? 'text-red-600' : 'text-green-600'}`}>
                                    {formatCurrency(newMonthlyTotal)}
                                </span>
                            </div>
                            {monthlyExceeded && (
                                <p className="text-xs text-red-600 mt-2 font-medium">
                                    ⚠️ {t('invoices.monthlyLimitExceeded')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={`flex items-center justify-end ${isRTL ? 'space-x-reverse' : 'space-x-3'} p-4 border-t border-gray-200 bg-gray-50`}>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                        {t('invoices.cancelInvoice')}
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
                    >
                        {t('invoices.continueAnyway')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CashThresholdWarningModal;
